import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/sections/Header";
import { ArticleCta } from "@/components/ArticleCta";
import { Breadcrumbs } from "@/pages/RoutePage";
import { pillarForPost, rankRelated, pillarPath } from "@/lib/seoArchitecture";
import Footer from "@/components/sections/Footer";
import Seo, { buildArticleSchema, buildBreadcrumbSchema, ORGANIZATION_SCHEMA, SITE_URL } from "@/components/Seo";
import { ArrowLeft, Clock } from "lucide-react";
import { PostBody } from "@/components/PostBody";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const related = rankRelated(allPosts, post);
  const pillar = pillarForPost(post);
  const crumbs = post ? [{ name: "Home", url: "/" }, ...(pillar ? [{ name: pillar.label, url: pillar.path }] : [{ name: "Resources", url: "/resources" }]), { name: post.title, url: `/blog/${post.slug}` }] : [];
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setPost(null);
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => active && setPost(r.data))
      .catch((e) => {
        if (!active) return;
        setError(e?.response?.status === 404 ? "not-found" : "error");
      })
      .finally(() => active && setLoading(false));
    axios
      .get(`${API}/posts`)
      .then((r) => {
        if (!active) return;
        setAllPosts(r.data || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <main
      data-testid="blog-post-page"
      className="min-h-screen bg-[#F5EFE7] text-[#451C25]"
    >
      {post && (
        <Seo
          title={post.seo_title || `${post.title} | Destino Europe Resources`}
          description={post.meta_description || post.excerpt || (post.body || "").slice(0, 180)}
          image={post.cover_url}
          imageAlt={post.cover_alt}
          path={`/blog/${post.slug}`}
          type="article"
          jsonLd={[
            ORGANIZATION_SCHEMA,
            buildBreadcrumbSchema(crumbs.map((c) => ({ name: c.name, url: `${SITE_URL}${c.url}` }))),
            buildArticleSchema({
              title: post.title,
              description: post.excerpt,
              image: post.cover_url ? (post.cover_url.startsWith("http") ? post.cover_url : `${SITE_URL}${post.cover_url}`) : undefined,
              datePublished: post.created_at,
              dateModified: post.updated_at,
              url: `${SITE_URL}/blog/${post.slug}`,
            }),
          ]}
        />
      )}
      {error === "not-found" && (
        <Seo title="Article not found | Destino Europe" description="This article is not available." path={`/blog/${slug}`} noindex />
      )}
      <Header />

      <article className="pt-32 md:pt-40 pb-24 px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/resources"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] hover:text-[#451C25] transition-colors mb-10"
          >
            <ArrowLeft size={14} /> All articles
          </Link>

          {loading && (
            <div className="space-y-6 animate-pulse">
              <div className="h-6 w-1/3 bg-[#451C25]/10" />
              <div className="h-14 w-full bg-[#451C25]/10" />
              <div className="h-14 w-3/4 bg-[#451C25]/10" />
              <div className="h-80 bg-[#451C25]/10" />
            </div>
          )}

          {error === "not-found" && (
            <div data-testid="blog-not-found" className="py-20">
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">
                Article not found
              </h1>
              <p className="text-[#6B4954]">
                The article you're looking for may have moved.{" "}
                <Link
                  to="/resources"
                  className="text-[#451C25] underline underline-offset-4"
                >
                  Browse all articles
                </Link>
                .
              </p>
            </div>
          )}

          {post && (
            <>
              <div className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-6">
                {post.country_tag && <span>{post.country_tag}</span>}
                {post.country_tag && post.category && (
                  <span className="text-[#451C25]/25">·</span>
                )}
                {post.category && <span>{post.category}</span>}
                <span className="text-[#451C25]/25">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={12} /> {post.read_time || 6} min read
                </span>
              </div>
              {(post.updated_at || post.last_reviewed) && (
                <p data-testid="post-dates" className="text-[11px] tracking-[0.12em] uppercase text-[#897756] mb-4" style={{ fontFamily: "Kanit, Arial, sans-serif" }}>
                  {post.created_at && <>Published {String(post.created_at).slice(0, 10)}</>}
                  {post.updated_at && <> · Last updated {String(post.updated_at).slice(0, 10)}</>}
                  {post.last_reviewed && <> · Last reviewed {String(post.last_reviewed).slice(0, 10)}</>}
                  {post.reviewed_by && <> · Reviewed by {post.reviewed_by}</>}
                </p>
              )}
              <div className="mb-6"><Breadcrumbs items={crumbs} /></div>
              <h1
                data-testid="blog-post-title"
                className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6"
              >
                {post.title}
              </h1>
              <p className="text-[#6B4954] text-lg md:text-xl leading-relaxed font-light mb-10">
                {post.excerpt}
              </p>
              {post.cover_url && (
                <div className="aspect-[16/9] overflow-hidden mb-12">
                  <img
                    src={post.cover_url}
                    alt={post.cover_alt || post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <PostBody body={post.body} slug={slug} />
            </>
          )}
        </div>

        {related.length > 0 && post && (
          <div className="max-w-7xl mx-auto mt-24 pt-16 border-t border-[#451C25]/15">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#6B4954] mb-8">
              Continue reading
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.id}
                  data-testid={`related-${p.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#EDE4D6] mb-5">
                    <img
                      src={p.cover_url}
                      alt={p.cover_alt || p.title}
                      className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-105"
                    />
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
                    {p.country_tag} · {p.category}
                  </div>
                  <h3 className="font-serif text-xl tracking-tight leading-tight group-hover:text-[#451C25] transition-colors">
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}
        {pillar && (
          <aside data-testid="related-to-your-move" className="mt-12 border-l-2 border-[#B3A17D] pl-5">
            <p className="text-[#897756] text-[11px] tracking-[0.26em] uppercase font-semibold" style={{ fontFamily: "Kanit, Arial, sans-serif" }}>Related to your move</p>
            <p className="mt-2 text-[#6B4954] text-[15px]" style={{ fontFamily: "'Noto Serif', Georgia, serif" }}>
              This article is part of our <a href={pillar.path} className="text-[#451C25] underline underline-offset-4 decoration-[#B3A17D]">{pillar.label}</a> path.
              {pillar.route !== "general" && <> Planning the wider move? Start with <a href={pillarPath("spain", "general")} className="text-[#451C25] underline underline-offset-4 decoration-[#B3A17D]">Moving to Spain from the USA</a>.</>}
            </p>
          </aside>
        )}
        <ArticleCta slug={slug} />
      </article>

      <Footer />
    </main>
  );
}
